/**
 * Analyze a binder: which cards make money if you sold them tomorrow.
 *
 * A screen of its own, never a pop-up, because a vendor's binder can hold six
 * hundred cards. The top says it in two numbers, profitable and potential loss,
 * at the vendor's own selling rate. Tabs jump straight to a list, and each card
 * carries what was paid, the profit or loss, and one note on where the price is
 * heading. The judging lives in utils/binderAnalysis so it can be tested and
 * changed without touching the screen.
 *
 * Cards with no recorded cost cannot be judged, and most vendors never tracked
 * cost, so that list is where the first visit is spent: each row takes the
 * number in place.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ChevronLeft, Printer, BarChart3 } from 'lucide-react';

import PrintLabelsSheet from '../components/print/PrintLabelsSheet';
import RoundPricesSheet from '../components/print/RoundPricesSheet';
import { labelQrUrl } from '../services/binderShare';
import {
  connect, isConnected, labelPrefs, printLabels, printerModel, rememberedPrinter,
} from '../services/labelPrinter';
import { useProfile } from '../context/ProfileContext';
import { useTheme } from '../context/ThemeContext';
import { supabase } from '../lib/supabase';
import { analyze, DEFAULT_RATE, RATES, money, signedMoney } from '../utils/binderAnalysis';
import haptics from '../utils/haptics';
import usePrintLabels from '../hooks/usePrintLabels';

const RATE_KEY = 'cc:analyze:rate';

function savedRate() {
  try {
    const v = Number(localStorage.getItem(RATE_KEY));
    return RATES.includes(v) ? v : DEFAULT_RATE;
  } catch {
    return DEFAULT_RATE;
  }
}

// A 48px thumbnail does not need the 600px scan. tcgdex serves a small one at
// the same path, which matters when a binder is six hundred rows long.
const thumb = (url) => (/assets\.tcgdex\.net\/.*\/high\.(webp|png|jpg)$/.test(url)
  ? url.replace(/\/high\.(webp|png|jpg)$/, '/low.$1')
  : url);

const TABS = [
  { key: 'all', label: 'All' },
  { key: 'profit', label: 'Profitable' },
  { key: 'loss', label: 'Potential loss' },
  { key: 'ask', label: 'No cost' },
];

const EMPTY = {
  all: 'This binder has no cards yet.',
  profit: 'Nothing here makes money at this rate.',
  loss: 'No potential losses at this rate.',
  ask: 'Every card has what you paid.',
};

export default function BinderAnalyze() {
  const { binderId } = useParams();
  const navigate = useNavigate();
  const { theme, isDark } = useTheme();
  const { activeProfile } = useProfile();
  const s = useMemo(() => makeStyles(theme, isDark), [theme, isDark]);

  const [binder, setBinder] = useState(null);
  const [rows, setRows] = useState(null);
  const [error, setError] = useState(null);
  const [rate, setRate] = useState(savedRate);
  const [tab, setTab] = useState('profit');
  const [printOpen, setPrintOpen] = useState(false);
  const [roundOpen, setRoundOpen] = useState(false);
  // Seeded from the live connection rather than always starting at false.
  // The printer stays linked between visits, and a screen that opens saying
  // "Connect and print" to somebody already connected is asking for a step
  // that has already happened.
  // One printer path for every screen; see hooks/usePrintLabels.
  const printLink = usePrintLabels({ qrUrlFor: qrUrlFor });

  const profileId = activeProfile?.id || null;

  useEffect(() => {
    if (!binderId) return undefined;
    let alive = true;
    setError(null);
    Promise.all([
      supabase.from('binders').select('id, name, share_token, is_private').eq('id', binderId).maybeSingle(),
      supabase.rpc('binder_analysis', { p_binder: binderId, ...(profileId ? { p_as: profileId } : {}) }),
    ]).then(([b, a]) => {
      if (!alive) return;
      if (a.error) {
        setError(a.error.message || 'This binder could not be analyzed.');
        setRows([]);
      } else {
        setRows(a.data || []);
      }
      setBinder(b.data || null);
    });
    return () => { alive = false; };
  }, [binderId, profileId]);

  const result = useMemo(() => analyze(rows || [], rate), [rows, rate]);
  const list = result[tab];

  // Open on a list that has something in it. A binder where nothing has a cost
  // yet would otherwise open on an empty Profitable tab, which reads as "you
  // make no money" when the truth is "we don't know what you paid".
  const tabChosen = useRef(false);
  useEffect(() => {
    if (rows === null || tabChosen.current) return;
    tabChosen.current = true;
    const first = ['profit', 'loss', 'ask'].find((k) => result[k].length > 0);
    setTab(first || 'all');
  }, [rows, result]);

  // Profitable and Potential loss can only be empty for two different reasons,
  // and the message has to say which one.
  const noCostsYet = result.profit.length + result.loss.length === 0 && result.ask.length > 0;

  const pickRate = (r) => {
    haptics.tap();
    setRate(r);
    try { localStorage.setItem(RATE_KEY, String(r)); } catch { /* per-device nicety only */ }
  };

  const pickTab = (key) => {
    haptics.tap();
    setTab(key);
  };

  // A cost typed in place moves the card to its list straight away.
  const onCostSaved = useCallback((cardId, cost) => {
    setRows((prev) => (prev || []).map((r) => (r.card_id === cardId ? { ...r, cost_basis: cost, cost_is_estimate: false } : r)));
  }, []);

  // Print goes through the rounding pass, the same as it does from the binder.
  // Analyze is where a vendor decides what to sell; the prices it is looking at
  // are the ones about to go on stickers, so they get the same review.
  const openPrint = () => {
    haptics.tap();
    // Ask the printer where it stands on the way in. A link made earlier in
    // the session is still good, and opening on "Connect and print" would ask
    // for a step already taken.
    printLink.refresh();
    setRoundOpen(true);
  };

  const printCards = useMemo(() => list.map((j) => ({
    id: j.row.card_id,
    name: j.row.name,
    set_name: j.row.set_name,
    set_number: j.row.set_number,
    variant: j.row.variant,
    grade: j.row.grade,
    short_code: j.row.short_code,
    price: j.price,
  })), [list]);

  // The same code every other label carries. This used to point at a shared
  // binder link with the card id on the end - a different destination from the
  // binder's own labels, and 85 characters where the short code is 20, which is
  // the difference between a QR that scans on a 12mm strip and one that does not.
  const qrUrlFor = useMemo(() => (card) => labelQrUrl(card), []);

  const runPrint = printLink.run;

  const savePrice = useCallback(async (cardId, price) => {
    setRows((prev) => (prev || []).map((r) => (r.card_id === cardId ? { ...r, price_now: price } : r)));
    try {
      await supabase.from('cards').update({ ask_price: price }).eq('id', cardId);
    } catch (e) {
      console.error('[analyze] could not save the typed price:', e);
    }
  }, []);

  const printLabel = tab === 'all'
    ? `Print all ${list.length} labels`
    : `Print ${list.length} ${list.length === 1 ? 'label' : 'labels'}`;

  return (
    <div style={s.page}>
      <div style={s.header}>
        <button style={s.back} onClick={() => { haptics.tap(); navigate(-1); }} aria-label="Back to the binder">
          <ChevronLeft size={24} />
        </button>
        <div style={s.headerText}>
          <div style={s.headerTitle}>Analyze</div>
          <div style={s.headerSub}>
            {binder?.name || 'Binder'}{rows ? ` · ${rows.length} ${rows.length === 1 ? 'card' : 'cards'}` : ''}
          </div>
        </div>
        <span style={s.headerSpacer} />
      </div>

      <div style={s.scroll}>
        {rows === null && <div style={s.loading}>Working out every card…</div>}

        {rows !== null && error && (
          <div style={s.empty}>
            <BarChart3 size={26} color={s.subColor} />
            <div style={s.emptyTitle}>Analyze did not load</div>
            <div style={s.emptyBody}>{error}</div>
          </div>
        )}

        {rows !== null && !error && (
          <>
            <section style={s.summary} aria-label="If you sold tomorrow">
              <div style={s.eyebrow}>If you sold tomorrow</div>
              <div style={s.split}>
                <button style={s.splitCell} onClick={() => pickTab('profit')}>
                  <span style={{ ...s.bigNum, color: theme.success }}>{result.profit.length}</span>
                  <span style={s.splitLabel}>Profitable</span>
                  <span style={{ ...s.splitMoney, color: theme.success }}>{signedMoney(result.profitTotal)}</span>
                </button>
                <button style={s.splitCell} onClick={() => pickTab('loss')}>
                  <span style={{ ...s.bigNum, color: theme.error }}>{result.loss.length}</span>
                  <span style={s.splitLabel}>Potential loss</span>
                  <span style={{ ...s.splitMoney, color: theme.error }}>{signedMoney(result.lossTotal)}</span>
                </button>
              </div>
              <div style={s.rateRow}>
                <span style={s.rateLabel}>You sell at</span>
                <div style={s.seg} role="group" aria-label="Selling rate">
                  {RATES.map((r) => (
                    <button
                      key={r}
                      style={rate === r ? { ...s.segBtn, ...s.segOn } : s.segBtn}
                      aria-pressed={rate === r}
                      onClick={() => pickRate(r)}
                    >
                      {Math.round(r * 100)}%
                    </button>
                  ))}
                </div>
              </div>
            </section>

            <div style={s.tabs} role="tablist" aria-label="Lists">
              {TABS.map((t) => {
                const count = result[t.key].length;
                const on = tab === t.key;
                const tone = t.key === 'profit' ? theme.success
                  : t.key === 'loss' ? theme.error
                    : t.key === 'ask' ? theme.warning : theme.text;
                const toneBg = t.key === 'profit' ? theme.successBg
                  : t.key === 'loss' ? theme.errorBg
                    : t.key === 'ask' ? theme.warningBg : theme.bgHover;
                return (
                  <button
                    key={t.key}
                    role="tab"
                    aria-selected={on}
                    style={on ? { ...s.tab, ...s.tabOn } : s.tab}
                    onClick={() => pickTab(t.key)}
                  >
                    <span style={{ ...s.tabCount, color: tone, backgroundColor: toneBg }}>{count}</span>
                    <span style={s.tabLabel}>{t.label}</span>
                  </button>
                );
              })}
            </div>

            {list.length === 0 ? (
              <div style={s.listEmpty}>
                {noCostsYet && (tab === 'profit' || tab === 'loss') ? (
                  <>
                    <div style={s.listEmptyTitle}>None of these cards has what you paid yet</div>
                    <div>Profit and loss need a cost. Add it on the No cost tab and each card sorts itself.</div>
                    <button style={s.listEmptyBtn} onClick={() => pickTab('ask')}>Go to No cost</button>
                  </>
                ) : EMPTY[tab]}
              </div>
            ) : (
              <div style={s.list}>
                {list.map((j) => (
                  <AnalyzeRow
                    key={j.row.card_id}
                    j={j}
                    s={s}
                    theme={theme}
                    profileId={profileId}
                    onOpen={() => navigate(`/card/${j.row.card_id}`)}
                    onCostSaved={onCostSaved}
                  />
                ))}
              </div>
            )}
          </>
        )}
      </div>

      {rows !== null && !error && list.length > 0 && (
        <div style={s.bar}>
          <button style={s.cta} onClick={openPrint}>
            <Printer size={18} strokeWidth={2.2} />
            <span>{printLabel}</span>
          </button>
        </div>
      )}

      <RoundPricesSheet
        open={roundOpen}
        onClose={() => setRoundOpen(false)}
        cards={printCards}
        binderName={binder?.name}
        printerName={rememberedPrinter()?.name || printerModel()}
        linked={printLink.linked}
        linking={printLink.linking}
        printing={printLink.printing}
        printedCount={printLink.done}
        error={printLink.error}
        onSettings={() => setPrintOpen(true)}
        onPriceSaved={savePrice}
        onPrint={runPrint}
      />

      <PrintLabelsSheet
        open={printOpen}
        onClose={() => setPrintOpen(false)}
        cards={printCards}
        binderName={binder?.name || 'Binder'}
        theme={theme}
        qrUrlFor={qrUrlFor}
      />
    </div>
  );
}

function AnalyzeRow({ j, s, theme, profileId, onOpen, onCostSaved }) {
  const { row, price, cost, profit, note, list } = j;
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);

  const save = async () => {
    const n = Number(String(value).replace(/[$,\s]/g, ''));
    if (!Number.isFinite(n) || n < 0 || String(value).trim() === '') {
      setSaveError('Enter what you paid, like 8.50');
      return;
    }
    setSaving(true);
    setSaveError(null);
    const { data, error } = await supabase.rpc('set_card_cost', {
      p_card: row.card_id,
      p_cost: n,
      ...(profileId ? { p_as: profileId } : {}),
    });
    setSaving(false);
    if (error) {
      setSaveError('That did not save. Try again.');
      return;
    }
    haptics.success();
    setEditing(false);
    onCostSaved(row.card_id, data ?? n);
  };

  return (
    <article style={s.row}>
      <button style={s.rowMain} onClick={onOpen} aria-label={`Open ${row.name}`}>
        {row.image_url
          ? <img src={thumb(row.image_url)} alt="" style={s.art} loading="lazy" decoding="async" />
          : <span style={s.artBlank} />}
        <span style={s.info}>
          <span style={s.nameLine}>
            <span style={s.name}>{row.name}</span>
            {row.grade && <span style={s.cond}>{row.grade}</span>}
          </span>
          {cost != null && profit != null ? (
            <span style={s.paid}>
              {row.cost_is_estimate ? 'Paid about ' : 'Paid '}
              <b style={s.paidStrong}>{money(cost)}</b>
              {' · '}
              <b style={{ color: profit >= 0 ? theme.success : theme.error }}>{signedMoney(profit)}</b>
            </span>
          ) : (
            <span style={s.paid}>{price == null ? 'No price yet' : 'Paid unknown'}</span>
          )}
          {note && <span style={s.note}>{note}</span>}
        </span>
        <span style={s.price}>{price == null ? '—' : money(price)}</span>
      </button>

      {list === 'ask' && price != null && !editing && (
        <button style={s.addCost} onClick={() => { haptics.tap(); setEditing(true); }}>
          Add what you paid
        </button>
      )}

      {editing && (
        <div style={s.costEditor}>
          <div style={s.costField}>
            <span style={s.costDollar}>$</span>
            <input
              id={`cost-${row.card_id}`}
              style={s.costInput}
              inputMode="decimal"
              placeholder="0.00"
              value={value}
              onChange={(e) => setValue(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') save(); }}
              aria-label={`What you paid for ${row.name}`}
              autoFocus
            />
          </div>
          <button style={s.costSave} onClick={save} disabled={saving}>{saving ? 'Saving' : 'Save'}</button>
          <button style={s.costCancel} onClick={() => { setEditing(false); setSaveError(null); }}>Cancel</button>
          {saveError && <div style={s.costError}>{saveError}</div>}
        </div>
      )}
    </article>
  );
}

const makeStyles = (theme, isDark) => {
  // Secondary text has to hold contrast on both grounds: the dark theme's
  // textSecondary is too dim on black and the light theme's textMuted too pale
  // on white, so each takes the stronger of its two.
  const sub = isDark ? theme.textMuted : theme.textSecondary;
  return {
    subColor: sub,
    page: {
      position: 'fixed',
      inset: 0,
      backgroundColor: theme.bg,
      color: theme.text,
      display: 'flex',
      flexDirection: 'column',
      zIndex: 900,
      paddingTop: 'var(--safe-area-top, 0px)',
      fontVariantNumeric: 'tabular-nums',
    },
    header: {
      display: 'grid',
      gridTemplateColumns: '44px 1fr 44px',
      alignItems: 'center',
      gap: '8px',
      padding: '8px 10px',
      borderBottom: `1px solid ${theme.border}`,
      flex: '0 0 auto',
    },
    back: {
      width: 44,
      height: 44,
      borderRadius: 12,
      border: 'none',
      backgroundColor: 'transparent',
      color: theme.text,
      display: 'grid',
      placeItems: 'center',
      cursor: 'pointer',
      padding: 0,
    },
    headerText: { textAlign: 'center', minWidth: 0 },
    headerTitle: { fontSize: 17, fontWeight: 800, color: theme.text },
    headerSub: {
      fontSize: 12,
      color: sub,
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      marginTop: 1,
    },
    headerSpacer: { width: 44 },
    scroll: {
      flex: 1,
      minHeight: 0,
      overflowY: 'auto',
      WebkitOverflowScrolling: 'touch',
      padding: '14px 14px 24px',
      display: 'flex',
      flexDirection: 'column',
      gap: 12,
    },
    loading: { color: sub, fontSize: 15, textAlign: 'center', padding: '48px 0' },

    summary: {
      backgroundColor: theme.bgCard,
      border: `1px solid ${theme.border}`,
      borderRadius: 18,
      padding: 16,
      display: 'flex',
      flexDirection: 'column',
      gap: 12,
      boxShadow: theme.shadowCard,
      flex: '0 0 auto',
    },
    eyebrow: {
      fontSize: 12,
      fontWeight: 700,
      letterSpacing: '0.08em',
      textTransform: 'uppercase',
      color: sub,
    },
    split: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 },
    splitCell: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'flex-start',
      gap: 3,
      padding: 0,
      border: 'none',
      backgroundColor: 'transparent',
      color: theme.text,
      cursor: 'pointer',
      textAlign: 'left',
      fontFamily: 'inherit',
    },
    bigNum: { fontSize: 34, fontWeight: 800, lineHeight: 1 },
    splitLabel: { fontSize: 15, fontWeight: 600, color: theme.text },
    splitMoney: { fontSize: 16, fontWeight: 700 },
    rateRow: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: 10,
      borderTop: `1px solid ${theme.border}`,
      paddingTop: 12,
    },
    rateLabel: { fontSize: 14, color: sub },
    seg: {
      display: 'inline-flex',
      backgroundColor: theme.bgSheet,
      border: `1px solid ${theme.border}`,
      borderRadius: 10,
      padding: 3,
      gap: 2,
    },
    segBtn: {
      fontFamily: 'inherit',
      fontSize: 14,
      fontWeight: 700,
      color: sub,
      backgroundColor: 'transparent',
      border: 'none',
      borderRadius: 7,
      padding: '7px 12px',
      minHeight: 34,
      cursor: 'pointer',
    },
    segOn: { backgroundColor: theme.bgCard, color: theme.text, boxShadow: theme.shadow },

    tabs: {
      position: 'sticky',
      top: -14,
      zIndex: 2,
      display: 'grid',
      gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
      gap: 4,
      backgroundColor: theme.bgSheet,
      border: `1px solid ${theme.border}`,
      borderRadius: 12,
      padding: 4,
      flex: '0 0 auto',
    },
    // Count over label, so four tabs share a phone's width without any of
    // them being cut off or scrolled out of sight.
    tab: {
      minWidth: 0,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'flex-start',
      gap: 4,
      fontFamily: 'inherit',
      fontSize: 12,
      fontWeight: 700,
      color: sub,
      backgroundColor: 'transparent',
      border: 'none',
      borderRadius: 9,
      padding: '8px 2px',
      minHeight: 52,
      cursor: 'pointer',
    },
    tabOn: { backgroundColor: theme.bgCard, color: theme.text, boxShadow: theme.shadow },
    tabLabel: { lineHeight: 1.15, textAlign: 'center' },
    tabCount: { fontSize: 14, fontWeight: 800, borderRadius: 999, padding: '1px 8px', lineHeight: 1.3 },

    list: { display: 'flex', flexDirection: 'column', gap: 8 },
    listEmpty: {
      color: sub,
      fontSize: 15,
      lineHeight: 1.45,
      textAlign: 'center',
      padding: '36px 16px',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: 8,
    },
    listEmptyTitle: { fontSize: 16, fontWeight: 700, color: theme.text },
    listEmptyBtn: {
      marginTop: 6,
      fontFamily: 'inherit',
      fontSize: 15,
      fontWeight: 700,
      color: isDark ? theme.warning : theme.warningDark,
      backgroundColor: theme.warningBg,
      border: 'none',
      borderRadius: 10,
      padding: '10px 16px',
      cursor: 'pointer',
    },

    row: {
      backgroundColor: theme.bgCard,
      border: `1px solid ${theme.border}`,
      borderRadius: 14,
      boxShadow: theme.shadowCard,
      overflow: 'hidden',
      flex: '0 0 auto',
    },
    rowMain: {
      width: '100%',
      display: 'grid',
      gridTemplateColumns: '48px 1fr auto',
      alignItems: 'center',
      columnGap: 12,
      padding: '10px 12px 10px 10px',
      border: 'none',
      backgroundColor: 'transparent',
      color: theme.text,
      textAlign: 'left',
      fontFamily: 'inherit',
      cursor: 'pointer',
    },
    art: { width: 48, height: 67, objectFit: 'cover', borderRadius: 4, backgroundColor: theme.bgSheet, display: 'block' },
    artBlank: { width: 48, height: 67, borderRadius: 4, backgroundColor: theme.bgSheet, display: 'block' },
    info: { minWidth: 0, display: 'flex', flexDirection: 'column', gap: 3 },
    nameLine: { display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 },
    name: {
      fontSize: 16,
      fontWeight: 700,
      lineHeight: 1.25,
      color: theme.text,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      minWidth: 0,
    },
    cond: {
      flex: '0 0 auto',
      fontSize: 11,
      fontWeight: 800,
      color: theme.text,
      border: `1.5px solid ${theme.text}`,
      borderRadius: 5,
      padding: '0 4px',
      lineHeight: 1.4,
    },
    paid: { fontSize: 13, color: sub },
    paidStrong: { color: theme.text, fontWeight: 700 },
    note: { fontSize: 12, color: sub, lineHeight: 1.35 },
    price: { fontSize: 22, fontWeight: 800, letterSpacing: '-0.01em', color: theme.text, textAlign: 'right' },

    addCost: {
      display: 'block',
      width: '100%',
      padding: '10px 12px',
      border: 'none',
      boxShadow: `inset 0 1px 0 ${theme.border}`,
      backgroundColor: theme.warningBg,
      color: isDark ? theme.warning : theme.warningDark,
      fontFamily: 'inherit',
      fontSize: 14,
      fontWeight: 700,
      cursor: 'pointer',
    },
    costEditor: {
      display: 'flex',
      flexWrap: 'wrap',
      rowGap: 6,
      alignItems: 'center',
      gap: 8,
      padding: '10px 12px 12px',
      borderTop: `1px solid ${theme.border}`,
    },
    costField: {
      flex: '1 1 0',
      minWidth: 0,
      display: 'flex',
      alignItems: 'center',
      gap: 4,
      backgroundColor: theme.bgInput,
      border: `1px solid ${theme.borderHover}`,
      borderRadius: 10,
      padding: '0 10px',
    },
    costDollar: { fontSize: 16, fontWeight: 700, color: sub },
    // 16px keeps iOS from zooming the page when the field takes focus.
    costInput: {
      flex: 1,
      minWidth: 0,
      fontFamily: 'inherit',
      fontSize: 16,
      fontWeight: 700,
      color: theme.text,
      backgroundColor: 'transparent',
      border: 'none',
      outline: 'none',
      padding: '10px 0',
    },
    costSave: {
      fontFamily: 'inherit',
      fontSize: 15,
      fontWeight: 700,
      color: '#ffffff',
      backgroundColor: theme.accent,
      border: `1px solid ${theme.accent}`,
      borderRadius: 10,
      padding: '10px 16px',
      cursor: 'pointer',
    },
    costCancel: {
      fontFamily: 'inherit',
      fontSize: 15,
      fontWeight: 600,
      color: sub,
      backgroundColor: 'transparent',
      border: 'none',
      padding: '10px 6px',
      cursor: 'pointer',
    },
    costError: { flexBasis: '100%', fontSize: 13, color: theme.error },

    bar: {
      flex: '0 0 auto',
      borderTop: `1px solid ${theme.border}`,
      backgroundColor: theme.bg,
      padding: '10px 14px calc(12px + var(--safe-area-bottom, 0px))',
    },
    cta: {
      width: '100%',
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 8,
      fontFamily: 'inherit',
      fontSize: 16,
      fontWeight: 700,
      color: '#ffffff',
      backgroundColor: theme.success,
      border: `1px solid ${theme.success}`,
      borderRadius: 12,
      padding: '14px 12px',
      minHeight: 50,
      cursor: 'pointer',
    },

    empty: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: 8,
      textAlign: 'center',
      padding: '48px 24px',
    },
    emptyTitle: { fontSize: 16, fontWeight: 700, color: theme.text },
    emptyBody: { fontSize: 13, color: sub, lineHeight: 1.5, maxWidth: 280 },
  };
};
